import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload, Trash2, Video, CheckCircle2 } from "lucide-react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { Progress } from "@/components/ui/progress";

const readDuration = (file) => new Promise((resolve) => {
  const v = document.createElement("video");
  v.preload = "metadata";
  v.onloadedmetadata = () => { resolve(Number.isFinite(v.duration) ? Math.round(v.duration) : null); URL.revokeObjectURL(v.src); };
  v.onerror = () => resolve(null);
  v.src = URL.createObjectURL(file);
});

const uploadVideo = async (file, lessonId, slug, onProgress) => {
  const duration = await readDuration(file);
  const { data } = await api.post("/admin/videos/init", { lesson_id: lessonId, part_slug: slug, filename: file.name, size: file.size, content_type: file.type || "video/mp4", duration });
  for (let n = 0; n < data.chunks; n++) {
    const blob = file.slice(n * data.chunk_size, Math.min(file.size, (n + 1) * data.chunk_size));
    for (let attempt = 0; ; attempt++) {
      try {
        await api.put(`/admin/videos/${data.video_id}/chunks/${n}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
        break;
      } catch (e) {
        if (attempt >= 2) throw e;
      }
    }
    onProgress(Math.round(((n + 1) / data.chunks) * 100));
  }
  await api.post(`/admin/videos/${data.video_id}/complete`);
};

const fmtSize = (b) => (b > 1e9 ? `${(b / 1e9).toFixed(1)} GB` : b >= 1e6 ? `${Math.round(b / 1e6)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

const PartRow = ({ lessonId, part, onDone }) => {
  const input = useRef(null);
  const [pct, setPct] = useState(null);
  const [preview, setPreview] = useState(false);
  const testId = `${lessonId}-${part.slug}`;
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPct(0);
    try {
      await uploadVideo(file, lessonId, part.slug, setPct);
      toast.success(`Video added to "${part.title}"`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPct(null);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Remove the video from "${part.title}"?`)) return;
    try {
      await api.delete(`/admin/videos/${part.video.video_id}`);
      toast.success("Video removed");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <li data-testid={`video-part-${testId}`} className="border-b border-border py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-3">
        {part.video ? <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-accent" /> : <Video className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <div className="min-w-0 flex-1 basis-48">
          <div className="text-sm font-semibold">{part.title}</div>
          <div className="text-xs text-muted-foreground">{part.video ? `${part.video.filename} · ${fmtSize(part.video.size)}` : "No video yet"}</div>
        </div>
        {pct !== null ? <div className="w-40"><Progress value={pct} className="h-2" /><div data-testid={`video-progress-${testId}`} className="mt-1 text-right text-xs">{pct}%</div></div> : (
          <>
            {part.video && <button type="button" data-testid={`video-preview-${testId}`} onClick={() => setPreview(!preview)} className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold hover:bg-muted">{preview ? "Hide" : "Preview"}</button>}
            <button type="button" data-testid={`video-upload-${testId}`} onClick={() => input.current?.click()} className="inline-flex items-center gap-1.5 rounded-full bg-brand-primary px-3 py-1.5 text-xs font-semibold text-brand-primary-fg"><Upload className="h-3.5 w-3.5" />{part.video ? "Replace" : "Upload"}</button>
            {part.video && <button type="button" data-testid={`video-remove-${testId}`} onClick={remove} aria-label="Remove video" className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
          </>
        )}
        <input ref={input} type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={pick} data-testid={`video-file-${testId}`} />
      </div>
      {preview && part.video && <video controls src={`${BACKEND_URL}/api/videos/${part.video.video_id}/stream`} className="mt-3 w-full max-w-lg rounded-lg bg-black" />}
    </li>
  );
};

const Orphans = ({ onDone }) => {
  const { data = [] } = useQuery({ queryKey: ["admin-videos-orphans"], queryFn: async () => (await api.get("/admin/videos/orphans")).data });
  if (!data.length) return null;
  const remove = async (v) => {
    if (!window.confirm(`Remove ${v.filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/videos/${v.video_id}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div data-testid="video-orphans" className="mt-6 rounded-xl border-2 border-brand-action/50 bg-brand-action/5 p-5">
      <h3 className="font-heading font-bold">Videos from the earlier lesson parts</h3>
      <p className="mt-1 text-sm text-muted-foreground">The new content splits lessons into scenes, so these videos are not shown to learners. Upload the right clip to a scene below, then remove the old one.</p>
      <ul className="mt-3 space-y-2">{data.map((v) => (
        <li key={v.video_id} data-testid={`video-orphan-${v.video_id}`} className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span><span className="font-mono text-xs">{v.lesson_id}</span> · {v.part_slug} · {v.filename}</span>
          <button type="button" data-testid={`video-orphan-remove-${v.video_id}`} onClick={() => remove(v)} className="rounded-full border border-border px-3 py-1 text-xs font-semibold hover:border-destructive hover:text-destructive">Remove</button>
        </li>
      ))}</ul>
    </div>
  );
};

export const VideosTab = () => {
  const qc = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-videos"], queryFn: async () => (await api.get("/admin/videos/outline")).data });
  const [mod, setMod] = useState("M01");
  const m = data.find((x) => x.id === mod);
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-videos"] }); qc.invalidateQueries({ queryKey: ["admin-videos-orphans"] }); qc.invalidateQueries({ queryKey: ["lesson"] }); };
  const count = (mm) => mm.lessons.reduce((a, l) => a + l.parts.filter((p) => p.video).length, 0);
  const total = (mm) => mm.lessons.reduce((a, l) => a + l.parts.length, 0);
  return (
    <div data-testid="admin-videos-tab">
      <p className="max-w-2xl text-sm text-muted-foreground">Add one short video per lesson scene. Learners in Video mode see it; scenes without a video show "Video coming". MP4 recommended (H.264), up to 2 GB. Large files upload in small pieces, so keep this tab open until it reaches 100%.</p>
      <Orphans onDone={refresh} />
      <div className="mt-6 flex flex-wrap gap-2">
        {data.map((x) => (
          <button key={x.id} type="button" data-testid={`video-module-${x.id}`} onClick={() => setMod(x.id)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${mod === x.id ? "border-brand-primary bg-brand-primary/10 text-brand-primary" : "border-border hover:bg-muted"}`}>{x.id} · {count(x)}/{total(x)}</button>
        ))}
      </div>
      {m && (
        <div className="mt-6 space-y-4">
          {m.lessons.map((l) => (
            <section key={l.id} data-testid={`video-lesson-${l.id}`} className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-baseline justify-between gap-3"><h3 className="font-heading font-bold">{l.title}</h3><Link to={`/lessons/${l.id}`} data-testid={`video-open-lesson-${l.id}`} className="shrink-0 text-xs font-semibold text-brand-primary hover:underline">Open lesson as learner →</Link></div>
              <ul className="mt-2">{l.parts.map((p) => <PartRow key={p.slug} lessonId={l.id} part={p} onDone={refresh} />)}</ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};
